import {
    InteractionRequiredAuthError,
    type SilentRequest,
    type AccountInfo,
    type AuthenticationResult,
    type IPublicClientApplication,
    CacheLookupPolicy,
    BrowserAuthErrorCodes,
    type AuthError,
    ClientAuthErrorCodes,
} from '@azure/msal-browser-1p';
import { AccountSourceType } from 'owa-account-source-list-types';
import { logStartCoreUsage, logStartUsage } from 'owa-analytics-start';
import type { MailboxInfo } from 'owa-client-types';
import { isUrlPresent, isHosted } from 'owa-config';
import type { HeadersWithoutIterator } from 'owa-service/lib/RequestOptions';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import { getTypeHintFromMailboxInfo } from './getTypeHint';
import {
    getMsalInstance,
    getClientId,
    getEnableTokenPopUpWhenHosted,
} from './initializeMsalLibrary';
import { loginUserMsalInternal } from './loginUserMsal';
import { onInteractionRequiredAuthFailure } from './onInteractionRequiredAuthFailure';
import { InteractionType } from './utils/InteractionType';
import { getAccountFromMsal } from './utils/MsalAccounts';
import { getAuthority } from './utils/getAuthority';
import { getScopes, isDefaultScope } from './utils/getScopes';
import { getLogAuthErrorData, getLogAuthResultData } from './utils/logger';
import { msalAuthenticationResultToTokenResponse } from './utils/msalAuthenticationResultToTokenResponse';
import getClaims from './utils/getClaims';
import { getSilentRedirectUri } from './utils/getRedirectUri';
import { createAuthError } from '@azure/msal-common';
import { getGuid } from 'owa-guid';
import { getIsSigninRequired, setIsSigninRequired } from './utils/signinRequiredHelpers';
import { isMailboxSameAccountAsGlobalSettingsAccount } from 'owa-account-source-list-store';
import {
    msa_server_error_1003030,
    msa_server_error_70012,
    msa_server_error_90032,
} from './utils/constants';
import { getMsalV5BridgeState } from 'owa-tokenprovider-utils/lib/getMsalV5BridgeState';
import { version as msalVersion } from '@azure/msal-browser-1p';
import { getMsalVersion } from './getMsalVersion';
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';

const MSAL_RETRIABLE_ERRORS = [
    BrowserAuthErrorCodes.postRequestFailed,
    BrowserAuthErrorCodes.noNetworkConnectivity,
    ClientAuthErrorCodes.endpointResolutionError,
    BrowserAuthErrorCodes.timedOut ?? 'timed_out', // v5 name
    'monitor_window_timeout', // v4 name (same error, renamed in v5)
    msa_server_error_1003030,
    msa_server_error_70012,
    msa_server_error_90032,
];

const DEFAULT_MAX_ATTEMPT = 2;

/**
 * Request access token from MSAL for given resource url or scope. User should be logged in already before this API is called.
 * Support for interactive token acquisition is limited to CAE claim challenge remediation only. Until we support multi-account in OWA, user will be taken to the login flow if interactive auth is needed for primary account in OWA.
 */
export async function acquireAccessTokenMsal(
    mailboxInfo: MailboxInfo,
    apiName: string,
    resource?: string,
    scope?: string,
    headers?: HeadersWithoutIterator,
    correlationId?: string,
    wwwAuthenticateHeader?: string,
    requestClaims?: string
): Promise<TokenResponse | undefined> {
    if (getIsSigninRequired()) {
        // If sign in banner is displayed, do not attempt any network requests.
        return undefined;
    }

    const typeHint = getTypeHintFromMailboxInfo(mailboxInfo);
    if (typeHint === AccountSourceType.Other) {
        return {
            TokenResultCode: 2,
            SubErrorCode: 'UnsupportedAccountType',
        };
    }

    correlationId = correlationId ?? getGuid();
    const msalInstance = await getMsalInstance();
    const account = getAccountFromMsal(msalInstance, mailboxInfo);
    const claims = getClaims(headers, wwwAuthenticateHeader, requestClaims);
    let popupStartTime: number;
    let authResultPromise: Promise<AuthenticationResult>;

    if (!account) {
        logStartUsage('MsalAcquireTokenNoAccount', mailboxInfo, {
            accountType: typeHint,
            correlationId,
            msalVersion: getMsalVersion(),
        });
        authResultPromise = loginUserMsalInternal(
            correlationId,
            msalInstance,
            InteractionType.Silent,
            typeHint,
            undefined /*account*/,
            mailboxInfo,
            undefined /*username*/,
            resource,
            scope,
            undefined /*promptValue*/,
            claims
        );
    } else {
        authResultPromise = acquireTokenSilently(
            correlationId,
            msalInstance,
            account,
            typeHint,
            apiName,
            resource,
            scope,
            claims
        );
    }

    return (
        authResultPromise
            // 1st-level error handler: check error from authResultPromise and perform CAE remediation if necessary
            .catch(async error => {
                if (
                    error instanceof InteractionRequiredAuthError &&
                    (claims || getEnableTokenPopUpWhenHosted())
                ) {
                    // Initiate interactive request for CAE claim challenge remediation
                    const interactionWithPopup = isHosted();
                    // Log the start of the interactive request, before popup is displayed or page redirects away.
                    logStartUsage('MsalAcquireToken_Sampled', mailboxInfo, {
                        accountType: typeHint,
                        interactionType: interactionWithPopup
                            ? InteractionType.Popup
                            : InteractionType.Redirect,
                        isCAERemediation: !!claims,
                        isGetTokenPopUpEnabled: getEnableTokenPopUpWhenHosted(),
                        correlationId,
                        applicationId: getClientId(),
                        msalVersion: getMsalVersion(),
                    });
                    if (interactionWithPopup) {
                        popupStartTime = self.performance.now();
                        return msalInstance.acquireTokenPopup({
                            account: account ?? undefined,
                            scopes: getScopes(typeHint, resource, scope),
                            authority: getAuthority(typeHint),
                            redirectUri: getSilentRedirectUri(),
                            state: getMsalV5BridgeState(msalVersion),
                            claims,
                            correlationId,
                        });
                    } else {
                        await msalInstance.acquireTokenRedirect({
                            account: account ?? undefined,
                            scopes: getScopes(typeHint, resource, scope),
                            authority: getAuthority(typeHint),
                            claims,
                            correlationId,
                        });
                        throw createAuthError('NotRedirected'); // make TS happy
                    }
                }

                throw error;
            })
            .then(authResult => msalAuthenticationResultToTokenResponse(authResult, typeHint))
            // 2nd-level error handler: catch-all errors from CAE remediation flow or unhandled from authResultPromise
            .catch(error => {
                if (getEnableTokenPopUpWhenHosted() && popupStartTime !== undefined) {
                    const authority = getAuthority(typeHint);
                    const pageAlwaysVisible = self.document.visibilityState === 'visible';

                    logStartCoreUsage(
                        'MsalAcquireToken',
                        mailboxInfo,
                        getLogAuthErrorData(
                            InteractionType.Popup,
                            error as AuthError,
                            popupStartTime,
                            apiName,
                            getScopes(typeHint, resource, scope),
                            typeHint,
                            !!authority && isUrlPresent(authority),
                            !!claims /* isCAERemediationRequired */,
                            pageAlwaysVisible /* isPageVisible */,
                            getIsSigninRequired(),
                            !!account
                        )
                    );
                }
                // Show the sign in banner for interaction required auth errors and network errors for OWA default scope only.
                if (
                    !isHosted() &&
                    shouldShowSigninBanner(error) &&
                    isDefaultScope(typeHint, resource, scope) &&
                    isMailboxSameAccountAsGlobalSettingsAccount(mailboxInfo)
                ) {
                    if (!getIsSigninRequired()) {
                        // Log the event to track the start of the remediation flow

                        logStartUsage('MsalAcquireToken_Sampled', mailboxInfo, {
                            accountType: typeHint,
                            correlationId,
                            applicationId: getClientId(),
                            msalVersion: getMsalVersion(),
                            isSigninRequired: true,
                        });
                    }

                    // Display sign in banner for other interaction required errors
                    setIsSigninRequired(true);
                    onInteractionRequiredAuthFailure();
                }

                return {
                    TokenResultCode: 2,
                    SubErrorCode: error.errorCode,
                    ErrorMessage: error.errorMessage,
                };
            })
    );
}

export async function acquireTokenSilently(
    correlationId: string,
    msalInstance: IPublicClientApplication,
    account: AccountInfo,
    typeHint: AccountSourceType,
    apiName: string,
    resource?: string,
    scope?: string,
    claims?: string,
    attemptCount = 1
): Promise<AuthenticationResult> {
    const startTime = self.performance.now();

    const request: SilentRequest = {
        account,
        scopes: getScopes(typeHint, resource, scope),
        authority: getAuthority(typeHint),
        redirectUri: getSilentRedirectUri(),
        state: getMsalV5BridgeState(msalVersion),
        claims,
        correlationId,

        // msal-browser@3.26.1 has a regression in NAA where cacheLookupPolicy is not properly defaulted to
        // CacheLookupPolicy.Default. Explicitly pass Default here as a workaround until MSAL fix is in.
        cacheLookupPolicy: CacheLookupPolicy.Default,
    };

    let pageAlwaysVisible = self.document.visibilityState === 'visible';
    const visibilityListener = () => {
        if (self.document.visibilityState != 'visible') {
            pageAlwaysVisible = false;
        }
    };
    self.document.addEventListener('visibilitychange', visibilityListener);

    try {
        const authResult = await msalInstance.acquireTokenSilent(request);

        self.document.removeEventListener('visibilitychange', visibilityListener);

        logStartUsage(
            'MsalAcquireToken_Sampled',
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            getLogAuthResultData(
                InteractionType.Silent,
                authResult,
                startTime,
                typeHint,
                !!request.authority && isUrlPresent(request.authority),
                pageAlwaysVisible,
                request.scopes.join(' ')
            ),
            { sessionSampleRate: 0.1 }
        );

        return authResult;
    } catch (e) {
        // Determine if this is a retriable error.
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'e' is of type 'unknown'.
        // @ts-expect-error
        if (MSAL_RETRIABLE_ERRORS.includes(e.errorCode) && attemptCount < DEFAULT_MAX_ATTEMPT) {
            return acquireTokenSilently(
                correlationId,
                msalInstance,
                account,
                typeHint,
                apiName,
                resource,
                scope,
                claims,
                ++attemptCount
            );
        }

        const isCAERemediationRequired = e instanceof InteractionRequiredAuthError && !!claims;
        self.document.removeEventListener('visibilitychange', visibilityListener);

        logStartCoreUsage(
            'MsalAcquireToken',
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            getLogAuthErrorData(
                InteractionType.Silent,
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'AuthError'.
                // @ts-expect-error
                e,
                startTime,
                apiName,
                request.scopes,
                typeHint,
                !!request.authority && isUrlPresent(request.authority),
                isCAERemediationRequired,
                pageAlwaysVisible,
                getIsSigninRequired()
            )
        );

        throw e;
    }
}

function shouldShowSigninBanner(error: AuthError): boolean {
    return (
        error instanceof InteractionRequiredAuthError ||
        error.errorCode === BrowserAuthErrorCodes.postRequestFailed
    );
}
